import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Settings, Phone, MapPin, CheckCircle2, ListChecks, ArrowRight,
  Plus, Trash2, Save, RefreshCw, GripVertical, Edit2
} from "lucide-react";

export default function ConfigLlamadasPage() {
  const { getAuthHeaders } = useAuth();
  const API_URL = (process.env.REACT_APP_BACKEND_URL || "");

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable states
  const [ciudades, setCiudades] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [tiposValidacion, setTiposValidacion] = useState([]);
  const [proximasAcciones, setProximasAcciones] = useState([]);

  // New item inputs
  const [newCiudad, setNewCiudad] = useState("");
  const [newResultado, setNewResultado] = useState("");
  const [newValidacion, setNewValidacion] = useState({ id: "", label: "" });
  const [newAccion, setNewAccion] = useState("");

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/control-llamadas/config/all`, getAuthHeaders());
      const data = await res.json();
      setConfig(data);
      setCiudades(data.ciudades || []);
      setResultados(data.resultados || []);
      setTiposValidacion(data.tipos_validacion || []);
      setProximasAcciones(data.proximas_acciones || []);
    } catch (error) {
      console.error("Error fetching config:", error);
      toast.error("Error al cargar la configuración");
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Save functions
  const saveCiudades = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/control-llamadas/config/ciudades`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(ciudades)
      });
      if (res.ok) {
        toast.success("Ciudades actualizadas");
      } else {
        toast.error("Error al guardar");
      }
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const saveResultados = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/control-llamadas/config/resultados`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(resultados)
      });
      if (res.ok) {
        toast.success("Resultados actualizados");
      } else {
        toast.error("Error al guardar");
      }
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const saveTiposValidacion = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/control-llamadas/config/tipos-validacion`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(tiposValidacion)
      });
      if (res.ok) {
        toast.success("Tipos de validación actualizados");
      } else {
        toast.error("Error al guardar");
      }
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const saveProximasAcciones = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/control-llamadas/config/proximas-acciones`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(proximasAcciones)
      });
      if (res.ok) {
        toast.success("Próximas acciones actualizadas");
      } else {
        toast.error("Error al guardar");
      }
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // Add functions
  const addCiudad = () => {
    if (newCiudad.trim() && !ciudades.includes(newCiudad.trim())) {
      setCiudades([...ciudades, newCiudad.trim()]);
      setNewCiudad("");
    }
  };

  const addResultado = () => {
    if (newResultado.trim() && !resultados.includes(newResultado.trim())) {
      setResultados([...resultados, newResultado.trim()]);
      setNewResultado("");
    }
  };

  const addValidacion = () => {
    if (newValidacion.id.trim() && newValidacion.label.trim()) {
      const exists = tiposValidacion.some(t => t.id === newValidacion.id.trim().toLowerCase());
      if (!exists) {
        setTiposValidacion([...tiposValidacion, {
          id: newValidacion.id.trim().toLowerCase().replace(/\s+/g, "_"),
          label: newValidacion.label.trim(),
          activo: true
        }]);
        setNewValidacion({ id: "", label: "" });
      }
    }
  };

  const addAccion = () => {
    if (newAccion.trim() && !proximasAcciones.includes(newAccion.trim())) {
      setProximasAcciones([...proximasAcciones, newAccion.trim()]);
      setNewAccion("");
    }
  };

  // Remove functions
  const removeCiudad = (ciudad) => {
    setCiudades(ciudades.filter(c => c !== ciudad));
  };

  const removeResultado = (resultado) => {
    setResultados(resultados.filter(r => r !== resultado));
  };

  const removeValidacion = (id) => {
    setTiposValidacion(tiposValidacion.filter(t => t.id !== id));
  };

  const toggleValidacion = (id) => {
    setTiposValidacion(tiposValidacion.map(t => 
      t.id === id ? { ...t, activo: !t.activo } : t
    ));
  };

  const removeAccion = (accion) => {
    setProximasAcciones(proximasAcciones.filter(a => a !== accion));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <Sidebar />
        <div className="flex-1 ml-0 md:ml-64 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-14 md:pt-6 overflow-x-hidden" data-testid="config-llamadas-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-600 rounded-xl">
              <Settings className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Datos Llamadas Telefónicas</h1>
              <p className="text-sm text-slate-500">Configuración de opciones para Control de Llamadas</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchConfig}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Recargar
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="ciudades" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="ciudades" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Ciudades
            </TabsTrigger>
            <TabsTrigger value="resultados" className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Resultados
            </TabsTrigger>
            <TabsTrigger value="validaciones" className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Validaciones
            </TabsTrigger>
            <TabsTrigger value="acciones" className="flex items-center gap-2">
              <ArrowRight className="w-4 h-4" />
              Acciones
            </TabsTrigger>
          </TabsList>

          {/* Ciudades Tab */}
          <TabsContent value="ciudades">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Ciudades
                </CardTitle>
                <CardDescription>
                  Lista de ciudades disponibles para seleccionar en el registro de llamadas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Nueva ciudad..."
                    value={newCiudad}
                    onChange={(e) => setNewCiudad(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addCiudad()}
                  />
                  <Button onClick={addCiudad}>
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ciudades.map((ciudad) => (
                    <Badge
                      key={ciudad}
                      variant="secondary"
                      className="px-3 py-1.5 text-sm flex items-center gap-2"
                    >
                      {ciudad}
                      <button
                        onClick={() => removeCiudad(ciudad)}
                        className="hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={saveCiudades} disabled={saving}>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Resultados Tab */}
          <TabsContent value="resultados">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Resultados de Llamada
                </CardTitle>
                <CardDescription>
                  Opciones de resultado al finalizar una llamada
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Nuevo resultado..."
                    value={newResultado}
                    onChange={(e) => setNewResultado(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addResultado()}
                  />
                  <Button onClick={addResultado}>
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar
                  </Button>
                </div>
                <div className="space-y-2">
                  {resultados.map((resultado, index) => (
                    <div
                      key={resultado}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{resultado}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeResultado(resultado)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={saveResultados} disabled={saving}>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Validaciones Tab */}
          <TabsContent value="validaciones">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Tipos de Validación
                </CardTitle>
                <CardDescription>
                  Datos que se pueden validar durante una llamada. Activa o desactiva según necesidad.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="ID (ej: telefono_trabajo)"
                    value={newValidacion.id}
                    onChange={(e) => setNewValidacion({...newValidacion, id: e.target.value})}
                    className="w-1/3"
                  />
                  <Input
                    placeholder="Etiqueta (ej: Teléfono de Trabajo)"
                    value={newValidacion.label}
                    onChange={(e) => setNewValidacion({...newValidacion, label: e.target.value})}
                    className="flex-1"
                    onKeyPress={(e) => e.key === "Enter" && addValidacion()}
                  />
                  <Button onClick={addValidacion}>
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar
                  </Button>
                </div>
                <div className="space-y-2">
                  {tiposValidacion.map((tipo) => (
                    <div
                      key={tipo.id}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-slate-400" />
                        <div>
                          <span className="font-medium">{tipo.label}</span>
                          <span className="text-xs text-slate-500 ml-2">({tipo.id})</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-slate-500">Activo</Label>
                          <Switch
                            checked={tipo.activo}
                            onCheckedChange={() => toggleValidacion(tipo.id)}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeValidacion(tipo.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={saveTiposValidacion} disabled={saving}>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Acciones Tab */}
          <TabsContent value="acciones">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRight className="w-5 h-5" />
                  Próximas Acciones
                </CardTitle>
                <CardDescription>
                  Opciones de seguimiento después de una llamada
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Nueva acción..."
                    value={newAccion}
                    onChange={(e) => setNewAccion(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addAccion()}
                  />
                  <Button onClick={addAccion}>
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar
                  </Button>
                </div>
                <div className="space-y-2">
                  {proximasAcciones.map((accion) => (
                    <div
                      key={accion}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{accion}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAccion(accion)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={saveProximasAcciones} disabled={saving}>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
